import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, AlertCircle, CheckCircle2, FileText, Edit, Wand2 } from 'lucide-react';

interface ModuleQualityIssue {
  module_id: string;
  module_title: string;
  course_id: string;
  issue_type: string;
  has_audio: boolean;
}

const INVALID_PATTERNS = [
  { pattern: /^\d+\./, name: 'Numbered Step', example: '1. Introduction' },
  { pattern: /\.(js|py|ts|yml|yaml|json|xml|html|css)$/i, name: 'File Extension', example: 'config.yml' },
  { pattern: /^(sudo|npm|git|docker|pip|apt|yarn)/i, name: 'Command', example: 'npm install' },
  { pattern: /^[\[\{<]/, name: 'Code Block', example: '[code]' },
  { pattern: /^[0-9\.\-\s]{3,}$/, name: 'Only Numbers', example: '3.1.4' },
  { pattern: /^Module \d+$/i, name: 'Generic Title', example: 'Module 3' },
  { pattern: /^(Step|Part|Section) \d+$/i, name: 'Placeholder', example: 'Step 2' },
];

export const ModuleDataQuality = () => {
  const [issues, setIssues] = useState<ModuleQualityIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingModule, setEditingModule] = useState<string | null>(null);
  const [newTitle, setNewTitle] = useState('');
  const [autoFixing, setAutoFixing] = useState(false);
  const [fixResults, setFixResults] = useState<any>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadQualityIssues();
  }, []);

  const loadQualityIssues = async () => {
    setLoading(true);
    try {
      const { data: modules, error } = await supabase
        .from('course_modules')
        .select(`
          module_id,
          module_title,
          course_id,
          is_enabled
        `)
        .eq('is_enabled', true);

      if (error) throw error;

      // Check for audio
      const { data: audioData } = await supabase
        .from('module_audio')
        .select('module_id')
        .not('audio_url', 'is', null);

      const audioModuleIds = new Set(audioData?.map(a => a.module_id) || []);

      // Identify issues
      const qualityIssues: ModuleQualityIssue[] = [];
      modules?.forEach(module => {
        for (const { pattern, name } of INVALID_PATTERNS) {
          if (pattern.test(module.module_title)) {
            qualityIssues.push({
              module_id: module.module_id,
              module_title: module.module_title,
              course_id: module.course_id,
              issue_type: name,
              has_audio: audioModuleIds.has(module.module_id),
            });
            break;
          }
        }
      });

      setIssues(qualityIssues);
    } catch (error) {
      console.error('Error loading quality issues:', error);
      toast({
        title: 'Error',
        description: 'Failed to load module quality data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTitle = async (moduleId: string, courseId: string) => {
    if (!newTitle.trim()) {
      toast({
        title: 'Invalid Title',
        description: 'Please enter a valid title',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('course_modules')
        .update({ module_title: newTitle })
        .eq('module_id', moduleId)
        .eq('course_id', courseId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Module title updated successfully',
      });

      setEditingModule(null);
      setNewTitle('');
      loadQualityIssues();
    } catch (error) {
      console.error('Error updating title:', error);
      toast({
        title: 'Error',
        description: 'Failed to update module title',
        variant: 'destructive',
      });
    }
  };

  const handleDisableModule = async (moduleId: string, courseId: string) => {
    if (!confirm('Are you sure you want to disable this module? It will be hidden from students.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('course_modules')
        .update({ is_enabled: false })
        .eq('module_id', moduleId)
        .eq('course_id', courseId);

      if (error) throw error;

      toast({
        title: 'Module Disabled',
        description: 'Module has been disabled and hidden from students',
      });

      loadQualityIssues();
    } catch (error) {
      console.error('Error disabling module:', error);
      toast({
        title: 'Error',
        description: 'Failed to disable module',
        variant: 'destructive',
      });
    }
  };

  const handleAutoFix = async () => {
    // Filter only auto-fixable modules (Generic Title and Placeholder)
    const autoFixableIssues = issues.filter(
      issue => issue.issue_type === 'Generic Title' || issue.issue_type === 'Placeholder'
    );

    if (autoFixableIssues.length === 0) {
      toast({
        title: 'No Auto-Fixable Modules',
        description: 'All remaining issues require manual review',
        variant: 'default',
      });
      return;
    }

    if (!confirm(
      `🤖 This will automatically:\n\n` +
      `✨ Extract proper titles from module content\n` +
      `❌ Disable broken modules with error content\n\n` +
      `Processing ${autoFixableIssues.length} modules with generic/placeholder titles.\n\n` +
      `Continue?`
    )) {
      return;
    }

    setAutoFixing(true);
    setFixResults(null);

    try {
      // Collect module IDs to fix
      const moduleIds = autoFixableIssues.map(issue => issue.module_id);

      const { data, error } = await supabase.functions.invoke('auto-fix-module-titles', {
        body: { moduleIds }
      });

      if (error) throw error;

      setFixResults(data);
      
      toast({
        title: 'Auto-Fix Complete! 🎉',
        description: `Fixed ${data.extracted_titles} titles, disabled ${data.disabled_modules} broken modules`,
      });

      // Reload the issues
      await loadQualityIssues();
    } catch (error) {
      console.error('Error in auto-fix:', error);
      toast({
        title: 'Auto-Fix Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setAutoFixing(false);
    }
  };

  const handleForceFixAll = async () => {
    if (issues.length === 0) {
      toast({
        title: 'No Issues Found',
        description: 'All modules have valid titles',
        variant: 'default',
      });
      return;
    }

    if (!confirm(
      `🚀 FORCE FIX ALL REMAINING MODULES\n\n` +
      `This will use advanced synthetic title extraction for ALL ${issues.length} modules with invalid titles.\n\n` +
      `✨ Extracts proper titles from content (first 8-10 words)\n` +
      `🎯 Works on broken/error titles that normal auto-fix can't handle\n` +
      `❌ Disables modules with truly invalid content\n\n` +
      `After this, audio generation will process ALL fixed modules.\n\n` +
      `Continue?`
    )) {
      return;
    }

    setAutoFixing(true);
    setFixResults(null);

    try {
      // Collect ALL module IDs with issues
      const moduleIds = issues.map(issue => issue.module_id);

      const { data, error } = await supabase.functions.invoke('auto-fix-module-titles', {
        body: { moduleIds }
      });

      if (error) throw error;

      setFixResults(data);
      
      toast({
        title: 'Force Fix Complete! 🎉',
        description: `Fixed ${data.extracted_titles} titles, disabled ${data.disabled_modules} broken modules. Audio generation will start automatically.`,
        duration: 5000,
      });

      // Reload the issues
      await loadQualityIssues();
    } catch (error) {
      console.error('Error in force fix:', error);
      toast({
        title: 'Force Fix Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setAutoFixing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading quality data...</span>
      </div>
    );
  }

  const issuesWithoutAudio = issues.filter(i => !i.has_audio);
  const issuesWithAudio = issues.filter(i => i.has_audio);
  
  // Count only auto-fixable issues (Generic Title and Placeholder)
  const autoFixableCount = issues.filter(
    issue => issue.issue_type === 'Generic Title' || issue.issue_type === 'Placeholder'
  ).length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Module Data Quality</h1>
          <p className="text-muted-foreground mt-2">
            Identify and fix modules with invalid titles that prevent proper TTS audio generation
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={handleAutoFix}
            disabled={autoFixing || autoFixableCount === 0}
            size="lg"
            variant="outline"
            className="gap-2"
          >
            {autoFixing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Wand2 className="h-4 w-4" />
                Quick Fix All ({autoFixableCount})
              </>
            )}
          </Button>
          
          {issues.length > autoFixableCount && (
            <Button
              onClick={handleForceFixAll}
              disabled={autoFixing || issues.length === 0}
              size="lg"
              className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {autoFixing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Wand2 className="h-4 w-4" />
                  Force Fix All ({issues.length})
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Auto-Fix Results */}
      {fixResults && (
        <Alert className="bg-green-500/10 border-green-500">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          <AlertDescription>
            <div className="space-y-2">
              <p className="font-semibold">✅ Fix Complete! 🎉</p>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Titles Extracted:</span>
                  <span className="ml-2 font-bold text-green-500">{fixResults.extracted_titles}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Modules Disabled:</span>
                  <span className="ml-2 font-bold text-orange-500">{fixResults.disabled_modules}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Skipped:</span>
                  <span className="ml-2 font-bold">{fixResults.skipped_modules}</span>
                </div>
              </div>
              {fixResults.extracted_titles > 0 && (
                <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
                  <p className="text-sm font-semibold text-blue-400">🎵 Audio Generation Starting Automatically</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The auto-resume system will detect these fixed modules and generate audio for all {fixResults.extracted_titles} modules within 3-5 minutes. 
                    Monitor progress in Admin → Configuration → Generation tab.
                  </p>
                </div>
              )}
              {fixResults.skipped_modules > 0 && (
                <p className="text-sm text-muted-foreground mt-2">
                  ⚠️ {fixResults.skipped_modules} modules could not be auto-fixed and may need manual review
                </p>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* Info Alert when issues exist */}
      {issues.length > 0 && !fixResults && (
        <Alert className="bg-blue-500/10 border-blue-500">
          <AlertCircle className="h-4 w-4 text-blue-500" />
          <AlertDescription>
            <p className="text-sm">
              <strong>💡 How to fix these modules:</strong>
            </p>
            <ul className="text-sm mt-2 space-y-1 ml-4 list-disc">
              <li><strong>Quick Fix All:</strong> Automatically fixes generic/placeholder titles only ({autoFixableCount} modules)</li>
              <li><strong>Force Fix All:</strong> Uses advanced extraction to fix ALL remaining issues including broken/error titles ({issues.length} modules)</li>
              <li><strong>Manual Fix:</strong> Click "Edit" on individual modules to set custom titles</li>
            </ul>
            <p className="text-xs text-muted-foreground mt-3">
              ⚡ After fixing, audio generation starts automatically via the auto-resume cron (runs every 3 minutes)
            </p>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="text-sm text-muted-foreground">Total Issues</p>
              <p className="text-3xl font-bold">{issues.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-orange-500" />
            <div>
              <p className="text-sm text-muted-foreground">Missing Audio</p>
              <p className="text-3xl font-bold">{issuesWithoutAudio.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Has Audio (Need Fix)</p>
              <p className="text-3xl font-bold">{issuesWithAudio.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Issues Table */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Issues ({issues.length})</TabsTrigger>
          <TabsTrigger value="no-audio">Missing Audio ({issuesWithoutAudio.length})</TabsTrigger>
          <TabsTrigger value="patterns">By Pattern</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module Title</TableHead>
                  <TableHead>Course ID</TableHead>
                  <TableHead>Issue Type</TableHead>
                  <TableHead>Audio Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issues.map((issue) => (
                  <TableRow key={`${issue.course_id}-${issue.module_id}`}>
                    <TableCell className="font-medium">
                      {editingModule === issue.module_id ? (
                        <Input
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          placeholder="Enter new title"
                          className="max-w-md"
                        />
                      ) : (
                        issue.module_title
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{issue.course_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{issue.issue_type}</Badge>
                    </TableCell>
                    <TableCell>
                      {issue.has_audio ? (
                        <Badge className="bg-green-500">Has Audio</Badge>
                      ) : (
                        <Badge variant="destructive">No Audio</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {editingModule === issue.module_id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateTitle(issue.module_id, issue.course_id)}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingModule(null);
                                setNewTitle('');
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingModule(issue.module_id);
                                setNewTitle(issue.module_title);
                              }}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDisableModule(issue.module_id, issue.course_id)}
                            >
                              Disable
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="no-audio" className="space-y-4">
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Module Title</TableHead>
                  <TableHead>Course ID</TableHead>
                  <TableHead>Issue Type</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issuesWithoutAudio.map((issue) => (
                  <TableRow key={`${issue.course_id}-${issue.module_id}`}>
                    <TableCell className="font-medium">{issue.module_title}</TableCell>
                    <TableCell className="font-mono text-xs">{issue.course_id}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{issue.issue_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingModule(issue.module_id);
                          setNewTitle(issue.module_title);
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Fix Title
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="patterns" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {INVALID_PATTERNS.map(({ name, example }) => {
              const count = issues.filter(i => i.issue_type === name).length;
              return (
                <Card key={name} className="p-4">
                  <h3 className="font-semibold">{name}</h3>
                  <p className="text-sm text-muted-foreground">Example: {example}</p>
                  <p className="text-2xl font-bold mt-2">{count} modules</p>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
